import {
  GeneralResourceDetail,
  IntegerString,
  PracticeContentDataFields,
  PracticeContentDataTypes,
  PracticeDetailFields,
  TraitId,
} from '@cibo/core'
import { usePracticesContent, useUpdateMultipleFields } from '@cibo/landmanager'
import { DataGridPro, FriendlyError, ResponsiveDialog } from '@cibo/ui'
import Star from '@mui/icons-material/Star'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-pro'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { prop } from 'ramda'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PROGRAMS_QUERY_KEY,
  useProgramEngagementDetails,
  useProgramModel,
  useUpdateProgramEngagementDetails,
} from '../../queries'
import { useEqipEngagementFieldsWithConcerns } from '../../queries/useEqipRecommendations'
import { usePracticeDrawer } from '../PracticeDrawer'
import {
  practiceAddOrRemoveColumn,
  practiceDescriptionColumn,
  practicePointsColumn,
} from './PracticesListColumns'
import { CombinedPractice, PracticesListProps } from './practicesListTypes'

export const PracticesList = <T extends GeneralResourceDetail = GeneralResourceDetail>({
  engagement,
  onError,
  onSuccess,
  onUpdating,
  recommendationsPointsSummary,
  resourceIds: [resourceId],
  selectedPoolName,
  selectedPractices,
  setSelectedPractices,
}: PracticesListProps<T>) => {
  const { palette } = useTheme()
  const queryClient = useQueryClient()
  const { t } = useTranslation('@cibo/programs/PracticesList')
  const updateFields = useUpdateMultipleFields()
  const fieldsWithConcerns = useEqipEngagementFieldsWithConcerns(engagement.id)
  const { enqueueSnackbar } = useSnackbar()

  const programModel = useProgramModel({ programId: engagement.programId })
  const selectionPractice = programModel.data?.benchmarks?.find(
    ({ benchmark }) => benchmark === 'practiceSelectionApply'
  )

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  })
  const [dialogState, setDialogState] = useState<{
    open: boolean
    practice?: PracticeContentDataFields
  }>({ open: false, practice: undefined })
  const { setDrawerState } = usePracticeDrawer()

  const [isChecked, setIsChecked] = useState(false)

  const updateEngagementDetails = useUpdateProgramEngagementDetails<T>({
    engagementId: engagement.id,
  })
  const details = useProgramEngagementDetails({
    engagementId: engagement.id,
    detailRequirements: [{ traitId: 'eqipRecommendedPracticesIntent', dataType: 'workflow' }],
  })

  const pools = recommendationsPointsSummary.data?.poolReports.map(prop('rankingPool'))
  const selectedPool = pools?.find(({ name }) => name === selectedPoolName)

  const practiceContent = usePracticesContent()

  useEffect(() => {
    setSelectedPractices((details?.data?.[0]?.result as string[]) || [])
  }, [details.dataUpdatedAt])

  const practicesContentItems = practiceContent.data?.items as unknown as PracticeContentDataTypes[]
  const practices = useMemo<CombinedPractice[]>(() => {
    const relevantPractices: CombinedPractice[] = []

    practicesContentItems?.forEach((allPractices: PracticeContentDataTypes) => {
      const matchingPractice = recommendationsPointsSummary.data?.practicePointsByPoolName(
        (allPractices.fields as unknown as PracticeDetailFields).practiceId
      )

      if (matchingPractice) {
        relevantPractices.push({ ...allPractices, points: matchingPractice })
      }
    })

    relevantPractices.sort((practiceA: CombinedPractice, practiceB: CombinedPractice) => {
      if (selectedPoolName) {
        const pointEffectDiff =
          (practiceB.points[selectedPoolName]?.effect || -0) -
          (practiceA.points[selectedPoolName]?.effect || -0)

        if (pointEffectDiff !== 0) {
          return pointEffectDiff
        }
        if (
          practiceA.points[selectedPoolName] !== undefined &&
          practiceB.points[selectedPoolName] === undefined
        )
          return -1
        if (
          practiceA.points[selectedPoolName] === undefined &&
          practiceB.points[selectedPoolName] !== undefined
        )
          return 1

        const addonA = practiceA.points[selectedPoolName]?.isAddon
        const addonB = practiceB.points[selectedPoolName]?.isAddon
        if (addonB && !addonA) return 1
        if (addonA && !addonB) return -1
        return 0
      } else {
        return 0
      }
    })

    return relevantPractices
  }, [practiceContent.dataUpdatedAt, recommendationsPointsSummary.dataUpdatedAt, selectedPoolName])

  const onClickSelect = (practiceId: string) => {
    const newSelection = [...selectedPractices, practiceId]

    setSelectedPractices(newSelection)

    onUpdating?.()
    updateEngagementDetails
      .mutateAsync({
        engagementId: engagement.id,
        details: [
          {
            traitId: 'eqipRecommendedPracticesIntent',
            result: newSelection.length ? newSelection : undefined,
          } as T,
        ],
      })
      .then(() => {
        onSuccess && onSuccess()
        queryClient.refetchQueries({
          queryKey: [PROGRAMS_QUERY_KEY.EQIP_RECOMMENDATIONS, resourceId],
        })
      })
      .catch(onError)
  }
  const onClickDeselect = (practiceId: string) => {
    const newSelection = selectedPractices.filter(id => id !== practiceId)
    setSelectedPractices(newSelection)
    onUpdating?.()
    const fieldUpdates =
      fieldsWithConcerns.data &&
      fieldsWithConcerns.data
        .map(field => {
          const selectedPractices = field.selectedPractices ?? []
          if (selectedPractices?.includes(practiceId as IntegerString)) {
            const newPractices = selectedPractices.filter((a: string) => a !== practiceId)
            const value =
              newPractices.length > 0 ? { input: { selectedPractices: newPractices } } : {}
            return {
              resourceId: field.id,
              details: [
                {
                  traitId: 'eqipSelectedPractices' as TraitId,
                  ...value,
                  year: selectionPractice?.details[0].year,
                },
              ],
            }
          } else {
            return undefined
          }
        })
        .filter(val => val !== undefined)
    const promises = [
      // remove practice from selected practices
      updateEngagementDetails.mutateAsync({
        engagementId: engagement.id,
        details: newSelection.length
          ? [
              {
                traitId: 'eqipRecommendedPracticesIntent',
                result: newSelection,
              } as T,
            ]
          : [
              {
                traitId: 'eqipRecommendedPracticesIntent',
              } as T,
            ],
      }),
      // @ts-ignore
      !!fieldUpdates && updateFields.mutateAsync(fieldUpdates),
    ]
    Promise.all(promises)
      .then(() => {
        onSuccess && onSuccess()
        fieldsWithConcerns.refetch()
      })
      .catch(args => {
        enqueueSnackbar(t('updateFailed'), { variant: 'error' })
        onError && onError(args)
      })
  }

  const onOpenDrawer = (practice: CombinedPractice) =>
    setDrawerState({
      open: true,
      practice,
      selectInteraction: {
        currentPracticeIsSelected: selectedPractices.includes(
          (practice.fields as unknown as PracticeDetailFields).practiceId
        ),
        onClickSelect: ({ value }) => onClickSelect(value),
      },
    })

  const columns = useMemo(
    () =>
      [
        practiceDescriptionColumn({
          t,
          onOpenDrawer,
          selectedPoolName,
        }),
        practicePointsColumn({
          t,
          recommendationsPointsSummary: recommendationsPointsSummary.data,
        }),
        practiceAddOrRemoveColumn({
          t,
          selectedPractices,
          onClickSelect,
          onClickDeselect,
          setDialogState,
          pending: updateFields.isPending,
          disabled: details.data?.[0]?.immutable,
        }),
      ].filter(Boolean) as GridColDef<CombinedPractice>[],
    [
      recommendationsPointsSummary.dataUpdatedAt,
      selectedPractices,
      selectedPoolName,
      updateFields.isPending,
    ]
  )

  const handleCloseDialog = () => {
    setIsChecked(false)
    setDialogState({ open: false, practice: undefined })
  }
  const handleCheckBox = (event: ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked)
  }

  if (practiceContent.isError) {
    return <FriendlyError message={t('errorMessage')} />
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h6" component="h2">
        {t('practiceSelectionTitle')}
      </Typography>
      {!!selectedPool?.addonText && (
        <Alert
          icon={
            <Star
              sx={{ alignSelf: 'center' }}
              fontSize="large"
              // @ts-ignore mui hasn't appropriately typed the 'color' prop to accept extended themes
              color="rating"
            />
          }
          variant={palette.mode === 'dark' ? 'filled' : 'standard'}
          style={{ backgroundColor: palette.background.default }}
        >
          <Typography variant="body1" color="text.primary">
            {t('bonusTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {selectedPool.addonText}
          </Typography>
        </Alert>
      )}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          getRowHeight={() => 'auto'}
          pagination
          disableRowSelectionOnClick
          paginationModel={paginationModel}
          pageSizeOptions={[5]}
          onPaginationModelChange={setPaginationModel}
          columns={columns}
          // This use of `onRowClick` is only acceptable for accessibility reasons *because* there is also a button
          // in the row that executes the same action.
          onRowClick={params => onOpenDrawer(params.row)}
          rows={practices}
          loading={practiceContent.isPending || recommendationsPointsSummary.isPending}
          getRowId={row => row.sys.id}
        />
      </div>
      <ResponsiveDialog
        open={dialogState.open}
        onClose={handleCloseDialog}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DialogContent>
          <Stack spacing={3}>
            <Typography variant="h6" component="h2" data-testid="dialog-title">
              {t('dialogTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('dialogSubtitle')}
            </Typography>
            <Box
              padding={1.5}
              sx={{
                backgroundColor: 'background.default',
                overflowY: 'auto',
              }}
            >
              <Typography variant="body1">
                {(dialogState.practice?.fields as unknown as PracticeDetailFields)?.requirementText}
              </Typography>
            </Box>
            <FormControlLabel
              data-testid="confirmCheckbox"
              control={
                <Checkbox checked={isChecked} onChange={handleCheckBox} name="confirmCheckbox" />
              }
              label={t('confirmCheckboxLabel')}
            />
            <DialogActions>
              <Stack spacing={3} direction="row">
                <Button color="secondary" onClick={handleCloseDialog}>
                  {t('cancel')}
                </Button>
                <Button
                  color="primary"
                  data-testid="addPracticeModelBtn"
                  onClick={() => {
                    dialogState.practice?.fields?.practiceId &&
                      onClickSelect(dialogState.practice?.fields?.practiceId)
                    handleCloseDialog()
                  }}
                  variant="contained"
                  disabled={!isChecked}
                >
                  {t('addPractice')}
                </Button>
              </Stack>
            </DialogActions>
          </Stack>
        </DialogContent>
      </ResponsiveDialog>
    </Stack>
  )
}
